import { TableCaretIcon } from "@components/icon";
import { IRowProps } from "@components/table/Rows.types";
import Tooltip from "@components/tooltip/Tooltip";
import { composeRefHandlers, doesElementContainsElement } from "@utils/general";
import { removeWhiteSpace } from "@utils/string";
import React, { PureComponent } from "react";

import { IconSize, RowType } from "../../enums";
import { KeyName } from "../../keyName";
import TestIds from "../../testIds";
import { ActionColumn, GroupIconWrapper, RowStretcher, StyledStatusHighlight, StyledValueRow } from "./Rows.styles";

export class Row extends PureComponent<IRowProps> {
    /* We want to prevent onClick when user just wants to select some text for copy paste */
    selectedTextWhenMouseDown = "";
    _rowRef = React.createRef<HTMLDivElement>();

    handleRowClick = (event: React.MouseEvent | React.KeyboardEvent): boolean => {
        if (event.defaultPrevented
            // ignore clicks on <a> tag (DrilldownLink)
            || (event.target as HTMLElement).tagName === "A"
        ) {
            return false;
        }

        if (this.selectedTextWhenMouseDown === removeWhiteSpace(window.getSelection().toString())) {
            const { shiftKey, altKey, ctrlKey, metaKey } = event;
            this.props.onClick?.(this.props.id, this.props, { shiftKey, altKey, ctrlKey, metaKey });
        }

        this.selectedTextWhenMouseDown = "";

        if (!this.props.action && this.props.hasRows && this.props.onGroupToggle) {
            this.props.onGroupToggle(this.props.id);
        }

        return true;
    };

    handleMouseDown = (event: React.MouseEvent): void => {
        if (event.shiftKey) {
            // when shift key is pressed, we don't want to make selection in table (prevent default), but we
            // do make multi row selection, @see handleRowClick
            event.preventDefault();
        }

        const selection = window.getSelection();

        if (selection && doesElementContainsElement(this._rowRef.current, selection.anchorNode?.parentElement as Element)) {
            this.selectedTextWhenMouseDown = removeWhiteSpace(window.getSelection().toString());
        }
    };

    handleGroupIconClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        this.props.onGroupToggle?.(this.props.id);
        e.stopPropagation(); // prevent rowClick event
    };

    handleMouseEnter = (event: React.MouseEvent): void => {
        this.props.onMouseEnter?.({
            originalEvent: event,
            target: (event.target as HTMLDivElement).parentElement.parentElement,
            id: this.props.id,
            groupId: this.props.groupId,
            part: -1,
            customData: this.props.customData
        });
    };

    handleMouseLeave = (event: React.MouseEvent): void => {
        this.props.onMouseLeave?.({
            originalEvent: event,
            target: (event.target as HTMLDivElement).parentElement.parentElement,
            id: this.props.id,
            groupId: this.props.groupId,
            part: -1,
            customData: this.props.customData
        });
    };

    handleFocus = (event: React.FocusEvent): void => {
        this.props.onFocus?.({
            originalEvent: event,
            id: this.props.id,
            groupId: this.props.groupId,
            customData: this.props.customData
        });
    };

    handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === KeyName.Enter) {
            const handled = this.handleRowClick(e);

            // without the check, enter doesn't work on drilldown link
            if (handled) {
                e.preventDefault();
            }
        } else if (e.key === KeyName.Space) {
            this.props.hasRows && this.props.onGroupToggle?.(this.props.id);
            e.preventDefault();
        }
    };

    handleContextMenu = (event: React.MouseEvent): void => {
        this.props.onContextMenu?.(this.props.id, this.props, event);
    };

    handleDragStart = (event: React.DragEvent<HTMLDivElement>): void => {
        this.props.onDragStart?.(this.props.id, this.props, event);
    };

    // TODO: are 4 different test ids necessary
    dataTestId = (): string => {
        if (this.props.type === RowType.Aggregation) {
            return TestIds.TableAggregationRow;
        } else if (this.props.type === RowType.Group) {
            return TestIds.TableGroupRow;
        } else if (this.props.type === RowType.Merged) {
            return TestIds.TableMergedRow;
        }
        return TestIds.TableRow;
    };

    render() {
        return (
            <Tooltip isHidden={!this.props.tooltip}
                     content={this.props.tooltip}>
                {(ref) =>
                    <StyledValueRow onClick={this.handleRowClick}
                                    onMouseDown={this.handleMouseDown}
                                    onKeyDown={this.handleKeyDown}
                                    onMouseEnter={this.handleMouseEnter}
                                    onMouseLeave={this.handleMouseLeave}
                                    onContextMenu={!this.props.isDisabled ? this.handleContextMenu : null}
                                    onDragStart={this.handleDragStart}
                                    draggable={!this.props.isDisabled && !!this.props.onDragStart}
                                    onFocus={this.handleFocus}
                                    $selected={this.props.selected}
                                    $isList={this.props.isList}
                                    $isDisabled={this.props.isDisabled}
                                    $isHoverDisabled={this.props.isHoverDisabled}
                                    $isHighlighted={this.props.isHighlighted}
                                    $hasAction={!!this.props.action}
                                    $isOpen={this.props.open}
                                    $hasRows={this.props.hasRows}
                                    $type={this.props.type}
                                    $nextRow={this.props.nextRow}
                                    $nextRowIsSelected={this.props.nextRowIsSelected}
                                    $hasGroups={!!this.props.hierarchy}
                                    $isDivider={this.props.isDivider}
                                    $sticky={this.props.sticky}
                                    $customBackgroundColor={this.props.customBackgroundColor}
                                    $customBorderColor={this.props.customRowBorderColor}
                                    ref={composeRefHandlers(ref, this._rowRef)}
                                    key={this.props.id.toString()}
                                    $minWidth={this.props.minWidth}
                                    $offset={this.props.offset}
                                    $level={this.props.level}
                                    $isBold={this.props.isBold}
                                    data-testid={this.dataTestId()}
                                    data-groupid={this.props.groupId}
                                    data-id={this.props.dataId ?? this.props.id.toString()}
                                    aria-selected={!!this.props.selected}
                                    aria-expanded={this.props.open}
                                    role="row" {...this.props.passProps}>
                        {this.props.statusHighlight &&
                            <Tooltip content={this.props.statusHighlightTooltip} status={this.props.statusHighlight}>
                                {(ref) => (
                                    <StyledStatusHighlight ref={ref}
                                                           data-testid={TestIds.State}
                                                           $statusHighlight={this.props.statusHighlight}
                                                           $selected={this.props.selected}/>
                                )}
                            </Tooltip>
                        }
                        {(this.props.hasGroups || this.props.hierarchy) &&
                            this.props.hasRows &&
                                <GroupIconWrapper $isOpen={this.props.open} onClick={this.handleGroupIconClick}>
                                <TableCaretIcon width={IconSize.XS} height={IconSize.XS}/>
                            </GroupIconWrapper>
                        }
                        {this.props.children}
                        <RowStretcher/>
                        {this.props.action &&
                            <ActionColumn data-testid={TestIds.TableRowAction}>
                                {this.props.action}
                            </ActionColumn>
                        }
                    </StyledValueRow>}
            </Tooltip>
        );
    }
}